import React from 'react';
import { Button } from '@wds/button';
import { Typography } from '@wds/typography';
import { Card } from '@wds/card';
import { T, defineMessages } from '@sm/intl';
import { LogoWithText } from '../Logos';
import useStyles from './useStyles';

const COPY = defineMessages({
  SORRY_MESSAGE: {
    id: 'FourOhFourError.sorryMessage',
    defaultMessage: "We're sorry",
    description: '[Type: Label][Vis.: med] - We are sorry message',
  },
  CANT_FIND_PAGE: {
    id: 'FourOhFourError.cantFindPage',
    defaultMessage: "We can't find the page you're looking for.",
    description: '[Type: Label][Vis.: med] - Cant find page message',
  },
  CHECK_URL: {
    id: 'FourOhFourError.checkURL',
    defaultMessage:
      "Please check the URL you entered to make sure it's spelled correctly.",
    description: '[Type: Label][Vis.: med] - check URL subtitle',
  },
  HELP_FIND_WAY: {
    id: 'FourOhFourError.helpFindWay',
    defaultMessage:
      'Still not sure how to get to the page you want? Maybe we can help you find your way:',
    description: '[Type: Label][Vis.: med] - Help find your way subtitle',
  },
  SIGN_UP_TEXT: {
    id: 'FourOhFourError.signUpText',
    defaultMessage: 'Want to create your own survey?',
    description:
      '[Type: Label][Vis.: med] - Sign up free button description text',
  },
  SIGN_UP_FREE: {
    id: 'FourOhFourError.signUpFree',
    defaultMessage: 'Sign Up FREE',
    description: '[Type: Label][Vis.: med] - Sign up free button',
  },
  HOME: {
    id: 'FourOhFourError.home',
    defaultMessage: 'Home',
    description: '[Type: Label][Vis.: med] - Home link in 404 Error Message',
  },
  SITEMAP: {
    id: 'FourOhFourError.sitemap',
    defaultMessage: 'Sitemap',
    description: '[Type: Label][Vis.: med] - Sitemap link in 404 Error Message',
  },
  HELP_CENTER: {
    id: 'FourOhFourError.helpCenter',
    defaultMessage: 'Help Center',
    description:
      '[Type: Label][Vis.: med] - Help Center link in 404 Error Message',
  },
  TEMPLATES: {
    id: 'FourOhFourError.templates',
    defaultMessage: 'Templates',
    description:
      '[Type: Label][Vis.: med] - Templates link in 404 Error Message',
  },
  LEARN_MORE: {
    id: 'FourOhFourError.learnMore',
    defaultMessage: 'Learn More',
    description:
      '[Type: Label][Vis.: med] - Learn More link in 404 Error Message',
  },
});

export type FourOhFourErrorProps = {
  showLinks?: boolean;
};

/* The default component for 404 error message */
const FourOhFourError = ({
  showLinks = false,
}: FourOhFourErrorProps): React.ReactElement => {
  const { wrapper, messageWrapper, cardWrapper, linkList } = useStyles();

  return (
    <div className={wrapper} data-testid="FourOhFourError">
      <div className={messageWrapper}>
        <Typography variant="pageTitle" align="center">
          <T desc={COPY.SORRY_MESSAGE} />
        </Typography>
        <div className={cardWrapper}>
          <Card p={2}>
            <div className="wds-p-1">
              <Typography
                component="section"
                variant="sectionTitle"
                align="center"
              >
                <T desc={COPY.CANT_FIND_PAGE} />
              </Typography>
            </div>
            <div className="wds-p-1">
              <Typography component="div" variant="body" align="center">
                <p>
                  <T desc={COPY.CHECK_URL} />
                </p>
                <p>
                  <T desc={COPY.HELP_FIND_WAY} />
                </p>
              </Typography>
            </div>

            <div className="wds-p-1">
              <Typography component="div" variant="cardTitle" align="center">
                <T desc={COPY.SIGN_UP_TEXT} />
              </Typography>
            </div>

            <Button
              color="upgrade"
              href="/user/sign-up/?ut_source=404_page"
              label=""
            >
              <T desc={COPY.SIGN_UP_FREE} />
            </Button>

            {showLinks && (
              <Typography component="div" variant="bodySm" align="center">
                <ul className={linkList}>
                  <li>
                    <a href="/">
                      <T desc={COPY.HOME} />
                    </a>
                  </li>
                  <li>
                    <a href="/mp/sitemap/">
                      <T desc={COPY.SITEMAP} />
                    </a>
                  </li>
                  <li>
                    <a href="https://help.surveymonkey.com/">
                      <T desc={COPY.HELP_CENTER} />
                    </a>
                  </li>
                  <li>
                    <a href="/mp/survey-templates/">
                      <T desc={COPY.TEMPLATES} />
                    </a>
                  </li>
                  <li>
                    <a href="/mp/take-a-tour/">
                      <T desc={COPY.LEARN_MORE} />
                    </a>
                  </li>
                </ul>
              </Typography>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

/* A variant when showing 404 error as a page */
export const FourOhFourErrorPage = (): React.ReactElement => {
  const { pageWrapper, pageLogoLink } = useStyles();
  return (
    <div className={pageWrapper}>
      <a href="/" data-testid="404-page-logo" className={pageLogoLink}>
        <LogoWithText />
      </a>
      <FourOhFourError showLinks />
    </div>
  );
};

export default FourOhFourError;
